import {
    Container,
} from 'pixi.js';
import { Game } from '../../game';
import { AccessibilityPopover } from './accessibilityPopover';
import { Settings } from '../../settings';
import { Panel } from './panels/Panel';
import { TextDisplay } from './panels/TextDisplay';
import { BackgroundArt } from './panels/BackgroundArt';
import { Volume } from './panels/Volume';
import { AudioDescription } from './panels/AudioDescription';
import { ClosedCaptions } from './panels/ClosedCaptions';
import { SFXType } from './panels/SFXType';
import { CoordinateEntry } from './panels/CoordinateEntry';
import { Battery } from './panels/Battery';
import { Hints } from './panels/Hints';
import { Dwell } from './panels/Dwell';
import { ActiveButton, Button, FocusManager, Focusable, InteractiveWrapper, Switch } from '../../ui';
import { LoadContext, DisposableGroup } from '../../utils';
import { RadioButton, Slider } from './ui';


export class Menus extends Container
{
    private game:Game;
    private popover:AccessibilityPopover;
    private settings:Settings;

    private toggleGroup: {[name:string]: ActiveButton}; // Object, indexed by Group type enum. Menu button for the group.
    private activeGroup: Menus.Type;        // enum value of the active group.
    private panels: {[name:string]: Panel[]};   // Object, indexed by Group type enum. All panels for the group.
    private firstTime:boolean;

    private commonButtons:Button[];  // Reference to play button, for focus context.

    private audioDescriptions: {[name:string]: string[]};
    private panelIntros: {[name:string]: string};

    private disposables: DisposableGroup;

    public static preload()
    {
        LoadContext.current.add('Settings', './assets/accessibility/Settings{size}.json');
        Panel.preload();
        Switch.preload();
        RadioButton.preload();
        Slider.preload();

        TextDisplay.preload();
        BackgroundArt.preload();
        Volume.preload();
        AudioDescription.preload();
        ClosedCaptions.preload();
        SFXType.preload();
        CoordinateEntry.preload();
        Battery.preload();
        Hints.preload();
        Dwell.preload();
    }

    constructor( popover:AccessibilityPopover )
    {
        super();
        this.disposables = new DisposableGroup();
        this.popover = popover;
        this.game = popover.game;
        this.settings = this.game.settings;
        this.commonButtons = popover.buttons;

        const loader = LoadContext.current;
        this.firstTime = true;

        // Toggle button group.
        this.toggleGroup = {};
        const toggleTextStyleActive = {
            align: 'center',
            fontName: 'AsapMedium',
            fontSize: 43,
            tint: 0x000000,
            wordWrap: true,
            wordWrapWidth: 400
        }
        const toggleTextStyleNormal = {
            align: 'center',
            fontName: 'AsapMedium',
            fontSize: 43,
            tint: 0xFFFFFF,
            wordWrap: true,
            wordWrapWidth: 400
        }

        const blackTex = loader.getSpritesheet('Settings').textures['toggle-black'];
        const whiteTex = loader.getSpritesheet('Settings').textures['toggle-white'];
        const screensToggle = new ActiveButton(
            blackTex,
            {
                content: 'Screen Setup and\nAudio Description',
                style: toggleTextStyleNormal
            },
            whiteTex,
            {
                content: 'Screen Setup and\nAudio Description',
                style: toggleTextStyleActive
            }
        );
        screensToggle.position.set( 620, 216 );
        this.disposables.add(new InteractiveWrapper(screensToggle, {
            onHover: () => {
                this.game.vo.playHighlight( 'CYB_Panel_ScreenSetup' );
            },
            onTap: () => {
                this.displayGroup( Menus.Type.Screen );
            }
        }));
        this.addChild( screensToggle );
        this.toggleGroup[ Menus.Type.Screen ] = screensToggle;
        this.activeGroup = Menus.Type.Screen;

        // Sounds
        const soundsToggle = new ActiveButton(
            blackTex,
            {
                content: 'Sounds\nand Captioning',
                style: toggleTextStyleNormal
            },
            whiteTex,
            {
                content: 'Sounds\nand Captioning',
                style: toggleTextStyleActive
            }
        );
        soundsToggle.position.set( 1087, 216 );
        this.disposables.add(new InteractiveWrapper(soundsToggle, {
            onHover: () => {
                this.game.vo.playHighlight( 'CYB_Panel_Sounds' );
            },
            onTap: () => {
                this.displayGroup( Menus.Type.Sounds );
            }
        }));
        this.addChild( soundsToggle );
        this.toggleGroup[ Menus.Type.Sounds ] = soundsToggle;

        // Difficulty
        const difficultyToggle = new ActiveButton(
            blackTex,
            {
                content: 'Difficulty Levels,\nHints and Controls',
                style: toggleTextStyleNormal
            },
            whiteTex,
            {
                content: 'Difficulty Levels,\nHints and Controls',
                style: toggleTextStyleActive
            }
        );
        difficultyToggle.position.set( 1542, 216 );
        this.disposables.add(new InteractiveWrapper(difficultyToggle, {
            onHover: () => {
                this.game.vo.playHighlight( 'CYB_Panel_Difficulty' );
            },
            onTap: () => {
                this.displayGroup( Menus.Type.Difficulty );
            }
        }));
        this.addChild( difficultyToggle );
        this.toggleGroup[ Menus.Type.Difficulty ] = difficultyToggle;


        // Panels
        this.panels = {};
        this.panels[ Menus.Type.Screen ] = [];
        this.panels[ Menus.Type.Sounds ] = [];
        this.panels[ Menus.Type.Difficulty ] = [];

        // SCREEN
        const textDisplay = new TextDisplay( this.game );
        textDisplay.position.set( 380, 344 );
        this.panels[ Menus.Type.Screen ].push( textDisplay );

        const bgArt = new BackgroundArt( this.game );
        bgArt.position.set( 1110, 344 );
        this.panels[ Menus.Type.Screen ].push( bgArt );

        const ad = new AudioDescription( this.game );
        ad.position.set( 1112, 630 );
        this.panels[ Menus.Type.Screen ].push( ad );
        ad.on( "turnedOn", ()=>{
            this.playAudioDescription( true );
        });

        // SOUNDS
        const volume = new Volume( this.game );
        volume.position.set( 380, 344 );
        this.panels[ Menus.Type.Sounds ].push( volume );

        const captions = new ClosedCaptions( this.game );
        captions.position.set( 1110, 344 );
        this.panels[ Menus.Type.Sounds ].push( captions );

        const sfx = new SFXType( this.game );
        sfx.position.set( 1112, 584 );
        this.panels[ Menus.Type.Sounds ].push( sfx );

        // DIFFICULTY
        const coord = new CoordinateEntry( this.game );
        coord.position.set( 380, 344 );
        this.panels[ Menus.Type.Difficulty ].push( coord );

        const battery = new Battery( this.game );
        battery.position.set( 384, 614 );
        this.panels[ Menus.Type.Difficulty ].push( battery );

        const hints = new Hints( this.game );
        hints.position.set( 1262, 344 );
        this.panels[ Menus.Type.Difficulty ].push( hints );

        const dwell = new Dwell( this.game );
        dwell.position.set( 1262, 734 );
        this.panels[ Menus.Type.Difficulty ].push( dwell );

        // Audio description.
        this.audioDescriptions = {};
        const baseAD = ['CYB_Panel_MovePanel', 'CYB_Panel_Choose', 'CYB_Panel_SelectOption'];
        this.audioDescriptions[ Menus.Type.Screen ] = ["Cyberchase_Game_Motherboard_P2a", ...baseAD];
        this.audioDescriptions[ Menus.Type.Sounds ] = ["Cyberchase_Game_Motherboard_P2b", ...baseAD];
        this.audioDescriptions[ Menus.Type.Difficulty ] = ["Cyberchase_Game_Motherboard_P3", ...baseAD];

        this.panelIntros = {
            [Menus.Type.Screen]: 'CYB_Panel_ScreenSetup_SettingsButton',
            [Menus.Type.Sounds]: 'CYB_Panel_Sounds_SettingsButton',
            [Menus.Type.Difficulty]: 'CYB_Panel_Hints_SettingsButton'
        };

        for (const key in this.panels)
        {
            const panelsOfType = this.panels[key];
            for (const panel of panelsOfType)
            {
                this.addChild(panel);
                panel.visible = false;
            }
        }
    }

    playInfoAudio()
    {
        this.popover.game.vo.playVO('CYB_PanelHome_InfoDescription', true);
    }

    async displayGroup( whichGroup: Menus.Type )
    {
        // const wasFirstTime = this.firstTime;
        if( whichGroup != this.activeGroup || this.firstTime ) {
            this.firstTime = false;
            this.activeGroup = whichGroup;

            // Update button displays.
            for( let key in this.toggleGroup ) {
                this.toggleGroup[ key ].active = (whichGroup === key);
            }


            // Set visibility of all elements.
            for( let key in this.panels ) {
                const panelsOfType:Panel[] = this.panels[key];
                for (const panel of panelsOfType)
                {
                    panel.visible = key === this.activeGroup;
                }
            }
        }

        FocusManager.instance.popContext();
        let items:Focusable[] = [];
        for( let key in this.toggleGroup ) {
            items.push( this.toggleGroup[key] );
        }

        for( let panel of this.panels[ whichGroup ] ) {
            let focusItems:Focusable[] = panel.getFocusItems();
            for( let item of focusItems ) {
                items.push( item );
            }
        }

        for( let button of this.commonButtons ) {
            items.push( button );
        }

        FocusManager.instance.activateContext( items );

        // if (wasFirstTime)
        // {
            await this.playAudioDescription();
        // }
        await this.popover.game.vo.playVO( this.panelIntros[this.activeGroup], true );
    }

    public resetActiveGroup()
    {
        switch( this.activeGroup ) {
            case Menus.Type.Screen:
                this.settings.textSize.value = Settings.TextSize.MEDIUM;
                this.settings.textStyle.value = Settings.TextStyle.WHITE_ON_BLACK;
                this.settings.showBackgroundArt.value = true;
                this.settings.playAudioDescriptions.value = false;
            break;
            case Menus.Type.Sounds:
                this.game.app.state.musicVolume.value = 0.6;
                this.game.app.state.sfxVolume.value = 0.6;
                this.game.app.state.voVolume.value = 0.6;
                this.game.app.state.captionsMuted.value = false;        // Default to captions on?
                this.settings.sfxType.value = Settings.SfxStyle.SFX_1;
            break;
            case Menus.Type.Difficulty:
                this.settings.allowCoordinateEntry.value = true;
                this.settings.useBattery.value = true;
                this.settings.hintFrequency.value = Settings.HintFrequency.SOME;
                this.settings.showRestArea.value = false;
            break;
        }

        for( let panel of this.panels[ this.activeGroup ] ) {
            panel.updateToMatchSetting();
        }
    }

    public playAudioDescription( firstTime:boolean = false )
    {
        if( this.popover.game.settings.playAudioDescriptions.value )
        {
            let lines:string[] = this.audioDescriptions[ this.activeGroup ];
            if( firstTime ) {
                lines.push( "CYB_Launch_ADReplay" );
            }
            return this.popover.game.vo.playVO( lines, true );
        }
    }

    destroy()
    {
        this.disposables.dispose();
        super.destroy({children: true});
    }
}

export namespace Menus {
    /**
     * Group defines the different accessibility screens
     */
    export enum Type {
        Screen = "screen",
        Sounds = "sounds",
        Difficulty = "difficulty"
    }
}